import "./Quiz.css";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Col, Container, Modal, Row } from "react-bootstrap";
import { Alert, Button as AntButton, Table, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faPen,
  faRotateLeft,
} from "@fortawesome/free-solid-svg-icons";
import { TailSpin } from "react-loader-spinner";
import dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";
import { useToasts } from "../../contexts/toasts";
import { Quizzes } from "../../api/Quiz";
import { Quiz, QuizSubmission } from "../../types/Quiz";
import { formatQuizDate, QuizStatusTag } from "./QuizList";

export default function QuizDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToasts();
  const [quiz, setQuiz] = useState<Quiz>();
  const [submissions, setSubmissions] = useState<QuizSubmission[]>();
  const [error, setError] = useState(false);
  const [toRevoke, setToRevoke] = useState<QuizSubmission>();
  const [revoking, setRevoking] = useState(false);

  const revoke = useCallback(() => {
    if (!toRevoke || !quiz) return;
    setRevoking(true);
    Quizzes.revokeSubmission(quiz.id, toRevoke.userId).then(
      ({ data }) => {
        setRevoking(false);
        setSubmissions((prev) => prev?.filter((s) => s.id !== toRevoke.id));
        setQuiz((prev) =>
          prev ? { ...prev, submissions: Math.max(0, prev.submissions - 1) } : prev
        );
        setToRevoke(undefined);
        showToast(
          "revoke-submission-success",
          `Partecipazione annullata: stornati ${data.revoked} Get Move Points (ora l'utente ne ha ${data.giftPoints}). Potrà rifare il quiz.`,
          "",
          "success"
        );
      },
      (err) => {
        setRevoking(false);
        showToast(
          "revoke-submission-error",
          err?.response?.data?.message ||
            "Errore nell'annullamento della partecipazione, per favore riprova.",
          "",
          "danger"
        );
      }
    );
  }, [toRevoke, quiz, showToast]);

  const load = useCallback(() => {
    if (!id) return;
    setError(false);
    Promise.all([Quizzes.getQuiz(id), Quizzes.getSubmissions(id)]).then(
      ([quizRes, subsRes]) => {
        setQuiz(quizRes.data.quiz);
        setSubmissions(subsRes.data.list);
      },
      () => {
        setError(true);
        showToast("quiz-detail-error", "Errore nel recupero del quiz", "", "danger");
      }
    );
  }, [id, showToast]);

  useEffect(() => {
    load();
  }, [load]);

  const stats = useMemo(() => {
    if (!submissions || !quiz) return undefined;
    const participants = submissions.length;
    const pointsAwarded = submissions.reduce((sum, s) => sum + s.pointsEarned, 0);
    const avgCorrect =
      participants > 0
        ? submissions.reduce((sum, s) => sum + s.correctCount, 0) / participants
        : 0;
    const fullScore = submissions.filter(
      (s) => s.correctCount === s.totalQuestions
    ).length;
    return { participants, pointsAwarded, avgCorrect, fullScore };
  }, [submissions, quiz]);

  // Percentuale di risposte corrette per domanda.
  const perQuestion = useMemo(() => {
    if (!quiz || !submissions) return new Map<string, number>();
    const map = new Map<string, number>();
    quiz.questions.forEach((question) => {
      const correct = submissions.filter((s) =>
        s.answers.some((a) => a.questionId === question.id && a.correct)
      ).length;
      map.set(question.id, correct);
    });
    return map;
  }, [quiz, submissions]);

  const columns: ColumnsType<QuizSubmission> = [
    {
      title: "#",
      key: "rank",
      width: 50,
      render: (_value, _row, index) => index + 1,
    },
    {
      title: "Utente",
      key: "user",
      render: (_value, row) =>
        row.user ? `${row.user.name} ${row.user.familyName}` : row.userId,
    },
    {
      title: "Risposte corrette",
      key: "correct",
      render: (_value, row) => `${row.correctCount}/${row.totalQuestions}`,
      sorter: (a, b) => a.correctCount - b.correctCount,
    },
    {
      title: "Punti guadagnati",
      dataIndex: "pointsEarned",
      key: "points",
      sorter: (a, b) => a.pointsEarned - b.pointsEarned,
      render: (value: number, row) => (
        <Tag color={value > 0 ? "green" : "default"}>
          {value}/{row.maxPoints}
        </Tag>
      ),
    },
    {
      title: "Completato il",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (value: string) => dayjs(value).format("DD/MM/YYYY HH:mm"),
      sorter: (a, b) => dayjs(a.createdAt).valueOf() - dayjs(b.createdAt).valueOf(),
    },
    {
      title: "",
      key: "actions",
      width: 110,
      render: (_value, row) => (
        <AntButton
          size="small"
          danger
          icon={<FontAwesomeIcon icon={faRotateLeft} />}
          onClick={() => setToRevoke(row)}
        >
          Annulla
        </AntButton>
      ),
    },
  ];

  if (error) {
    return (
      <Container fluid="md" className="mt-5">
        <Alert type="error" showIcon message="Quiz non trovato" />
      </Container>
    );
  }

  if (!quiz || !submissions || !stats) {
    return (
      <div className="d-flex justify-content-center py-5 my-5">
        <TailSpin height={100} width={100} color="#f25d0b" radius="1" />
      </div>
    );
  }

  return (
    <Container fluid="md" className="mt-5 mb-5">
      <Row className="mb-2 align-items-center">
        <Col xs="auto">
          <AntButton
            type="text"
            icon={<FontAwesomeIcon icon={faArrowLeft} />}
            onClick={() => navigate("/quiz")}
          />
        </Col>
        <Col>
          <h4 className="mb-0">
            {quiz.title} <QuizStatusTag status={quiz.status} />
          </h4>
        </Col>
        <Col xs={6} md={4} className="text-end">
          <button
            className="button-primary"
            onClick={() => navigate(`/quiz/${quiz.id}/modifica`)}
          >
            <FontAwesomeIcon icon={faPen} />
            <span className="d-none d-sm-inline ms-2">Modifica</span>
          </button>
        </Col>
      </Row>
      <hr />

      <p className="text-muted mb-1">
        Dal <strong>{formatQuizDate(quiz.startDate)}</strong> al{" "}
        <strong>{formatQuizDate(quiz.endDate)}</strong>
      </p>
      {quiz.description && <p className="text-muted">{quiz.description}</p>}

      <div className="quiz-summary">
        <div className="quiz-summary-tile">
          <div className="quiz-summary-label">Partecipanti</div>
          <div className="quiz-summary-value">{stats.participants}</div>
        </div>
        <div className="quiz-summary-tile">
          <div className="quiz-summary-label">Punti assegnati</div>
          <div className="quiz-summary-value">{stats.pointsAwarded}</div>
        </div>
        <div className="quiz-summary-tile">
          <div className="quiz-summary-label">Media risposte corrette</div>
          <div className="quiz-summary-value">
            {stats.avgCorrect.toFixed(1)}/{quiz.questions.length}
          </div>
        </div>
        <div className="quiz-summary-tile">
          <div className="quiz-summary-label">Punteggio pieno</div>
          <div className="quiz-summary-value">{stats.fullScore}</div>
        </div>
      </div>

      <h6 className="text-primary mt-4">Domande ({quiz.questions.length}, {quiz.maxPoints} punti in palio)</h6>
      {quiz.questions.map((question, index) => (
        <div className="quiz-question-review" key={question.id}>
          <div className="d-flex justify-content-between align-items-start gap-2">
            <strong>
              {index + 1}. {question.text}
            </strong>
            <Tag>{question.points} pt</Tag>
          </div>
          <ul>
            {question.answers.map((answer) => (
              <li
                key={answer.id}
                className={answer.id === question.correctAnswerId ? "correct" : ""}
              >
                {answer.text}
                {answer.id === question.correctAnswerId && " ✓"}
              </li>
            ))}
          </ul>
          {stats.participants > 0 && (
            <div className="text-muted mt-2" style={{ fontSize: "0.85rem" }}>
              Risposta corretta da {perQuestion.get(question.id) || 0} su{" "}
              {stats.participants} (
              {Math.round(((perQuestion.get(question.id) || 0) / stats.participants) * 100)}
              %)
            </div>
          )}
        </div>
      ))}

      <h6 className="text-primary mt-4">Risultati</h6>
      {submissions.length === 0 ? (
        <Alert type="info" showIcon message="Nessun utente ha ancora completato il quiz." />
      ) : (
        <Table
          rowKey="id"
          size="small"
          dataSource={submissions}
          columns={columns}
          pagination={{ pageSize: 20, showSizeChanger: true }}
          scroll={{ x: true }}
        />
      )}

      <Modal show={!!toRevoke} onHide={() => !revoking && setToRevoke(undefined)}>
        <Modal.Header closeButton>
          <Modal.Title>Annulla partecipazione</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Vuoi annullare il quiz di{" "}
          <strong>
            {toRevoke?.user
              ? `${toRevoke.user.name} ${toRevoke.user.familyName}`
              : toRevoke?.userId}
          </strong>
          ?
          <div className="text-muted mt-2">
            Il risultato verrà cancellato e gli verranno stornati{" "}
            <strong>{toRevoke?.pointsEarned}</strong> Get Move Points (se nel
            frattempo ne ha riscattati, lo storno si ferma a zero). L'utente
            riceverà una notifica e potrà rifare il quiz finché è attivo.
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="light"
            size="sm"
            className="border"
            disabled={revoking}
            onClick={() => setToRevoke(undefined)}
          >
            Chiudi
          </Button>
          <Button variant="danger" size="sm" disabled={revoking} onClick={revoke}>
            {revoking ? (
              <TailSpin width={20} height={20} />
            ) : (
              <>
                <FontAwesomeIcon icon={faRotateLeft} />
                <span> Annulla partecipazione</span>
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
